'use client';

import React, { useEffect, useRef, useState } from 'react';
import confetti from 'canvas-confetti';
import PageHeader from '../../components/shared/PageHeader';
import PageFooter from '../../components/shared/PageFooter';

// Shared blue-ombre gradient stops, reused across all Get-in-touch icons.
// We give each icon its own unique gradient id so the stroke="url(#...)"
// reference is unambiguous when multiple icons share the same SVG namespace.
const OmbreStops = () => (
  <>
    <stop offset="0%" stopColor="#6ee7b7" />
    <stop offset="50%" stopColor="#06dffa" />
    <stop offset="100%" stopColor="#dcccfe" />
  </>
);

const InstagramIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <defs>
      <linearGradient id="ombre-ig" x1="0" y1="0" x2="1" y2="1">
        <OmbreStops />
      </linearGradient>
    </defs>
    <rect x="3" y="3" width="18" height="18" rx="5" ry="5" stroke="url(#ombre-ig)" />
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" stroke="url(#ombre-ig)" />
    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" stroke="url(#ombre-ig)" />
  </svg>
);

const TikTokIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
    <defs>
      <linearGradient id="ombre-tt" x1="0" y1="0" x2="1" y2="1">
        <OmbreStops />
      </linearGradient>
    </defs>
    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 00-.79-.05A6.34 6.34 0 003.15 15.2a6.34 6.34 0 0010.86 4.48V13.4a8.16 8.16 0 005.58 2.2V12.1a4.84 4.84 0 01-3.58-1.59V6.69h3.58z" stroke="url(#ombre-tt)" />
  </svg>
);

const MailIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <defs>
      <linearGradient id="ombre-mail" x1="0" y1="0" x2="1" y2="1">
        <OmbreStops />
      </linearGradient>
    </defs>
    <rect x="3" y="5" width="18" height="14" rx="2" ry="2" stroke="url(#ombre-mail)" />
    <polyline points="3,7 12,13 21,7" stroke="url(#ombre-mail)" />
  </svg>
);

const BlogIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <defs>
      <linearGradient id="ombre-blog" x1="0" y1="0" x2="1" y2="1">
        <OmbreStops />
      </linearGradient>
    </defs>
    <path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" stroke="url(#ombre-blog)" />
    <polyline points="14,3 14,9 20,9" stroke="url(#ombre-blog)" />
    <line x1="8" y1="13" x2="16" y2="13" stroke="url(#ombre-blog)" />
    <line x1="8" y1="17" x2="13" y2="17" stroke="url(#ombre-blog)" />
  </svg>
);

// Animated counter hook
const useCountUp = (end: number, duration: number, startCounting: boolean) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!startCounting) return;
    let start = 0;
    const increment = end / (duration / 16);
    const timer = setInterval(() => {
      start += increment;
      if (start >= end) {
        setCount(end);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 16);
    return () => clearInterval(timer);
  }, [end, duration, startCounting]);
  return count;
};

// Scroll-triggered fade-in hook
const useFadeIn = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return { ref, style: {
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? 'translateY(0)' : 'translateY(24px)',
    transition: 'opacity 0.6s ease, transform 0.6s ease',
  } as React.CSSProperties };
};

const GradientDivider = () => (
  <div className="about-divider" style={{
    height: '1px',
    background: 'linear-gradient(90deg, transparent 0%, rgba(168,139,250,0.3) 30%, rgba(168,139,250,0.5) 50%, rgba(168,139,250,0.3) 70%, transparent 100%)',
    margin: '8px 0',
  }} />
);

// Memories collage — composited offline by scripts/build-about-collage.py
// into /public/collage-composite.png. The on-page render is a single <img>;
// re-run the script after replacing source photos in /public/collage/.

const AboutContent = ({ embedded = false }: { embedded?: boolean }) => {
  const font = '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Helvetica Neue", sans-serif';

  // Fade-in refs for each section
  const missionFade = useFadeIn();
  const phoneFade = useFadeIn();
  const contactFade = useFadeIn();

  // Confetti canvas behind the founders video — fires once, 5s after mount,
  // matching the app's celebration palette (Nav logo / payment / divvy).
  const foundersConfettiRef = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    if (embedded) return;
    const timer = setTimeout(() => {
      if (!foundersConfettiRef.current) return;
      const fire = confetti.create(foundersConfettiRef.current, { resize: true });
      fire({
        particleCount: 120,
        spread: 80,
        startVelocity: 40,
        origin: { x: 0.5, y: 0.6 },
        colors: ['#6ee7b7', '#06dffa', '#dcccfe', '#667eea', '#764ba2'],
      });
    }, 5000);
    return () => clearTimeout(timer);
  }, [embedded]);

  // Parallax for the top collage — moves at 40% of scroll speed so it
  // feels like a fixed-ish backdrop, mirroring the landing page's drinks
  // hero image. The about page scrolls on window (Ionic's ion-content
  // isn't used here), so we listen on window directly. Skipped in
  // embedded mode since the collage isn't rendered there.
  const collageRef = useRef<HTMLImageElement>(null);
  // The mobile OTA bundle prunes the 3.4MB collage image to stay slim, so on
  // mobile we skip rendering it entirely (rather than 404 into a broken image).
  // Web keeps the full hero collage. The founders/items videos still ship.
  const isMobileBundle = process.env.NEXT_PUBLIC_IS_MOBILE === 'true';
  useEffect(() => {
    if (embedded) return;
    const handleScroll = () => {
      const img = collageRef.current;
      if (!img) return;
      img.style.transform = `translateY(${window.scrollY * 0.4}px)`;
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [embedded]);

  // Only play the founders + items-pay videos while they're mostly on
  // screen. Saves CPU/battery when scrolled away and avoids autoplay
  // running silently in the background.
  useEffect(() => {
    const videos = Array.from(
      document.querySelectorAll<HTMLVideoElement>('.founders-video, .about-phone-mockup video')
    );
    if (videos.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const v = entry.target as HTMLVideoElement;
          if (entry.isIntersecting) {
            v.play().catch(() => {});
          } else {
            v.pause();
          }
        }
      },
      { threshold: 0.5 }
    );
    videos.forEach((v) => observer.observe(v));
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <style>{`
        /* Founders intro video — portrait 9:16 source rendered as a
           rounded rectangle with the page's dark-purple outline. */
        .founders-video-wrap {
          width: 170px;
          aspect-ratio: 9 / 16;
          border-radius: 18px;
          border: 2px solid #0d0f18;
          outline: 3px solid #3d2870;
          outline-offset: 0;
          overflow: hidden;
          position: relative;
          z-index: 1;
          animation: foundersFloat 3.4s ease-in-out infinite;
        }
        /* Confetti canvas sits behind the video at z-index -1 within the
           about-founders-wrap stacking context (which has z-index: 0).
           Sized larger than the video so the burst spreads beyond it. */
        .founders-confetti-canvas {
          position: absolute;
          top: 50%;
          left: 50%;
          width: 520px;
          height: 480px;
          transform: translate(-50%, -55%);
          pointer-events: none;
          z-index: -1;
        }
        .founders-video {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }
        .founders-caption {
          margin: 14px 0 0;
          font-size: 14px;
          font-weight: 500;
          color: white;
          text-align: center;
          letter-spacing: 0.01em;
        }
        .founders-caption-sub {
          margin: 2px 0 0;
          font-size: 12px;
          font-weight: 400;
          color: rgba(255, 255, 255, 0.45);
          text-align: center;
          letter-spacing: 0.01em;
        }

        @keyframes aboutSparkle {
          0%, 100% { background-position: 200% center; }
          50% { background-position: -200% center; }
        }

        /* Memories collage — tight scrapbook band that spans the full viewport
           width and is fully covered by overlapping photos (no background
           shows through). A subtle dim overlay tints the whole band so it
           sits naturally on the page. */
        @keyframes aboutCollageFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        /* Collage is positioned against the outer page container (which is
           position: relative on the root div), anchored to the top of the
           page. PageHeader (z-index 100) and about-content-wrap (z-index
           2) float above it. The mask-image fades the bottom into the
           page background, mirroring the homepage hero image's fade. */
        .about-collage {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 360px;
          overflow: hidden;
          opacity: 0;
          animation: aboutCollageFadeIn 0.7s ease-out 0.15s both;
          z-index: 0;
          /* Decorative backdrop — let touches pass through to the body
             so the user's first-thumb-down at the top of the page
             scrolls instead of getting absorbed by this absolute layer. */
          pointer-events: none;
          -webkit-mask-image: linear-gradient(to bottom, black 0%, black 45%, transparent 100%);
          mask-image: linear-gradient(to bottom, black 0%, black 45%, transparent 100%);
        }
        .about-collage::after {
          content: '';
          position: absolute;
          inset: 0;
          /* Heavier vignette than the homepage — about-page collage gets
             stronger edge darkening + a light global dim layer so the
             photos read as moody backdrop rather than the focal point.
             The parent's mask still handles the hard bottom dissolve. */
          background:
            radial-gradient(ellipse at center, rgba(10, 21, 56, 0.18) 0%, rgba(10, 21, 56, 0.56) 55%, rgba(10, 21, 56, 0.91) 88%),
            linear-gradient(to bottom, rgba(13, 20, 36, 0.54) 0%, rgba(10, 21, 56, 0.24) 22%, rgba(10, 21, 56, 0.40) 45%, rgba(10, 21, 56, 0.78) 70%, rgba(10, 21, 56, 0.95) 88%, #0a1538 100%),
            linear-gradient(to right, rgba(13, 20, 36, 0.50) 0%, rgba(13, 20, 36, 0.14) 20%, rgba(13, 20, 36, 0.14) 80%, rgba(13, 20, 36, 0.50) 100%);
          pointer-events: none;
          z-index: 200;
        }
        @media (max-width: 767px) {
          .about-collage {
            height: 240px;
          }
        }
        .about-collage img {
          display: block;
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center top;
        }

        /* Mobile-only stand-in for the pruned collage: a soft aurora glow at
           the top that fades out before it reaches the content, so the top of
           the page dissolves smoothly into the wrapper gradient instead of
           showing a flat dark band. Matches the collage's 240px band + fade. */
        .about-collage-fallback {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 240px;
          pointer-events: none;
          z-index: 0;
          background:
            radial-gradient(130% 90% at 50% -25%,
              rgba(139, 108, 232, 0.32) 0%,
              rgba(101, 78, 188, 0.16) 34%,
              rgba(10, 21, 56, 0) 70%);
          -webkit-mask-image: linear-gradient(to bottom, black 0%, black 50%, transparent 100%);
          mask-image: linear-gradient(to bottom, black 0%, black 50%, transparent 100%);
        }

        /* Contact card hover */
        .about-contact-card {
          transition: transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1), background 0.2s ease, box-shadow 0.3s ease, border-color 0.2s ease;
        }
        .about-contact-card:hover {
          transform: translateY(-4px) scale(1.02);
          background: rgba(255,255,255,0.1) !important;
          box-shadow: 0 12px 32px rgba(0,0,0,0.25), 0 0 0 1px rgba(168,139,250,0.2);
          border-color: rgba(168,139,250,0.3) !important;
        }

        /* Phone mockup tilt + float — clean 2D rotateZ keeps the
           rectangle's edges equal-length (no perspective foreshortening),
           translateY pulse keeps the existing soft bounce. */
        @keyframes phonePulse {
          0%, 100% { transform: translateY(0); }
          50%      { transform: translateY(-6px); }
        }
        @keyframes foundersFloat {
          0%, 100% {
            box-shadow: 0 24px 55px rgba(10, 21, 56, 0.45), 0 0 50px rgba(168, 139, 250, 0.22), 0 0 90px rgba(6, 223, 250, 0.14);
            transform: rotate(5deg) translateY(0);
          }
          50% {
            box-shadow: 0 30px 65px rgba(10, 21, 56, 0.55), 0 0 70px rgba(168, 139, 250, 0.32), 0 0 110px rgba(6, 223, 250, 0.18);
            transform: rotate(5deg) translateY(-6px);
          }
        }

        /* Stat counter pop */
        @keyframes statPop {
          0% { transform: scale(0.8); opacity: 0; }
          60% { transform: scale(1.05); }
          100% { transform: scale(1); opacity: 1; }
        }


        /* Desktop responsive */
        .about-content-wrap {
          max-width: 600px;
          margin: 0 auto;
          padding: 0 24px;
          position: relative;
          z-index: 2;
        }
        .about-mission-section {
          text-align: center;
          /* Clears the nav and drops "Who we are" onto the faded lower half
             of the 240px collage band, the same relationship the desktop
             breakpoint gets from its 144px against a 360px band. At the old
             36px the heading landed on the busiest, fully-opaque part of the
             photos right under the nav. */
          margin-top: 88px;
          margin-bottom: 32px;
        }
        .about-mission-heading {
          font-size: 32px;
          animation: aboutSparkle 6s ease-in-out infinite;
        }
        /* Body copy matches the college-ambassadors page: Bricolage Grotesque
           at regular weight, so the paragraphs sit in the same family as the
           headings instead of switching to a second typeface mid-page. */
        .about-mission-subheading {
          font-size: 18px;
          font-weight: 600;
          letter-spacing: -0.005em;
          color: white;
          margin: 0 0 16px;
          line-height: 1.5;
          font-family: var(--font-bricolage), -apple-system, BlinkMacSystemFont, "SF Pro Text", "Helvetica Neue", sans-serif;
        }
        /* 16px is flat across breakpoints — the ambassadors page doesn't
           scale its body copy up on desktop, so neither does this. */
        .about-mission-body {
          font-size: 16px;
          font-weight: 400;
          font-family: var(--font-bricolage), -apple-system, BlinkMacSystemFont, "SF Pro Text", "Helvetica Neue", sans-serif;
        }
        .about-stats-bar {
          gap: 32px;
          padding: 28px 0;
        }
        .about-stat-value {
          font-size: 24px;
        }
        .about-phone-section {
          flex-direction: column;
          gap: 32px;
          padding: 36px 0;
          align-items: center;
          text-align: center;
        }
        .about-phone-heading {
          font-size: 27px;
          animation: aboutSparkle 6s ease-in-out infinite;
        }
        .about-phone-subheading {
          font-size: 18px;
          font-weight: 600;
          letter-spacing: -0.005em;
          color: white;
          margin: 0 0 16px;
          line-height: 1.5;
          font-family: var(--font-bricolage), -apple-system, BlinkMacSystemFont, "SF Pro Text", "Helvetica Neue", sans-serif;
        }
        .about-phone-body {
          font-size: 16px;
          font-weight: 400;
          font-family: var(--font-bricolage), -apple-system, BlinkMacSystemFont, "SF Pro Text", "Helvetica Neue", sans-serif;
        }
        .about-phone-mockup {
          width: 340px;
          min-width: 340px;
          order: 0;
        }
        /* Subtle dim over the founders video so the live action reads as
           supporting texture rather than competing with the heading +
           body. The items-pay phone mockup now bakes the bezel/dim into
           the video file itself, so it doesn't get this overlay. */
        .founders-video-wrap::after {
          content: '';
          position: absolute;
          inset: 0;
          background: rgba(10, 21, 56, 0.08);
          pointer-events: none;
          z-index: 2;
        }
        .about-phone-text {
          order: -1;
        }
        .about-contact-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
        }
        .about-divider {
          margin: 8px 0;
        }
        .about-section-heading {
          font-size: 32px;
        }

        @media (min-width: 768px) {
          .about-content-wrap {
            max-width: 960px !important;
            padding: 0 48px !important;
          }
          .about-mission-section {
            text-align: left !important;
            display: flex !important;
            flex-direction: row !important;
            gap: 48px !important;
            align-items: center !important;
            margin-top: 144px !important;
            margin-bottom: 48px !important;
          }
          .about-mission-text {
            flex: 1 !important;
            min-width: 0 !important;
          }
          .about-mission-heading {
            font-size: 32px !important;
            line-height: 1.4 !important;
          }
          .about-mission-subheading {
            font-size: 20px !important;
            margin: 0 0 20px !important;
          }
          .about-founders-wrap {
            margin-top: 24px !important;
            flex-shrink: 0 !important;
            width: 340px !important;
          }
          .founders-video-wrap {
            width: 220px !important;
          }
          .founders-caption {
            margin-top: 18px !important;
            font-size: 15px !important;
          }
          .founders-caption-sub {
            font-size: 13px !important;
          }
          .about-stats-bar {
            gap: 64px !important;
            padding: 40px 0 !important;
          }
          .about-stat-value {
            font-size: 36px !important;
          }
          .about-stat-label {
            font-size: 14px !important;
          }
          .about-phone-section {
            flex-direction: row !important;
            gap: 48px !important;
            padding: 56px 0 !important;
            align-items: center !important;
            text-align: left !important;
          }
          .about-phone-text {
            order: 0 !important;
          }
          .about-phone-mockup {
            order: 0 !important;
          }
          .about-phone-heading {
            font-size: 32px !important;
            line-height: 1.4 !important;
          }
          .about-phone-subheading {
            font-size: 20px !important;
            margin: 0 0 20px !important;
          }
          .about-phone-mockup {
            width: 440px !important;
            min-width: 440px !important;
            margin-right: 24px !important;
            margin-left: -120px !important;
          }
          .about-section-heading {
            font-size: 32px !important;
            margin-bottom: 8px !important;
          }
          .about-contact-section {
            padding-top: 32px !important;
            margin-bottom: 64px !important;
          }
          .about-contact-grid {
            grid-template-columns: 1fr 1fr 1fr 1fr !important;
            gap: 16px !important;
          }
          .about-contact-card {
            padding: 20px 16px !important;
          }
          .about-divider {
            margin: 16px 0 !important;
          }
        }

        /* When embedded in a modal, force mobile layout regardless of viewport */
        /* Pull the collage up to the very top of the modal by escaping
           IonContent's padding/scroll entirely (position:fixed). The
           SettingsModal IonContent has --padding-top: safe-area+64px
           to reserve room for the back-button + avatar overlay; absolute
           positioning inside the wrapper sat below that padding and left
           a dark band at the top. Fixed positioning lands the collage at
           the modal/viewport top regardless of any safe-area or default
           IonContent padding. Mask still fades the bottom into the page. */
        .about-embedded .about-collage {
          position: fixed !important;
          top: 0 !important;
          left: 0 !important;
          right: 0 !important;
          width: 100% !important;
          height: 240px !important;
        }
        .about-embedded .about-content-wrap {
          max-width: 100% !important;
          padding: 0 24px !important;
        }
        .about-embedded .about-mission-section {
          text-align: center !important;
          display: block !important;
          /* The About modal is edge-to-edge (--padding-top: 0 — see
             SettingsModal's dark-cutoff comment), so the heading must carry
             its own clearance for the status bar + the floating back/avatar
             row. PADDING, not margin: a top margin here collapses through
             about-content-wrap and the gradient wrapper (no top padding or
             border on either), shoving the whole gradient down and exposing
             the dark #0a0f32 modal body above it — the original "dark top"
             band, reintroduced. Padding keeps the wrapper at y=0 so the
             gradient + aurora paint behind the cleared space. */
          margin-top: 0 !important;
          padding-top: calc(env(safe-area-inset-top, 0px) + 80px) !important;
          margin-bottom: 0 !important;
        }
        .about-embedded .about-mission-heading {
          font-size: 25px !important;
        }
        .about-embedded .about-mission-subheading {
          font-size: 16px !important;
          margin: 0 0 12px !important;
        }
        .about-embedded .about-mission-body {
          font-size: 15px !important;
        }
        .about-embedded .about-founders-wrap {
          margin-top: 24px !important;
          width: 100% !important;
        }
        .about-embedded .about-stats-bar {
          gap: 24px !important;
          padding: 24px 0 !important;
        }
        .about-embedded .about-stat-value {
          font-size: 28px !important;
        }
        .about-embedded .about-stat-label {
          font-size: 12px !important;
        }
        .about-embedded .about-phone-section {
          flex-direction: column !important;
          gap: 16px !important;
          padding: 32px 0 !important;
          align-items: center !important;
          text-align: center !important;
        }
        .about-embedded .about-phone-heading {
          font-size: 25px !important;
        }
        .about-embedded .about-phone-subheading {
          font-size: 16px !important;
          margin: 0 0 12px !important;
        }
        .about-embedded .about-phone-body {
          font-size: 15px !important;
        }
        .about-embedded .about-phone-mockup {
          width: 180px !important;
          min-width: 180px !important;
        }
        .about-embedded .about-section-heading {
          font-size: 22px !important;
        }
        .about-embedded .about-contact-section {
          padding-top: 20px !important;
          margin-bottom: 140px !important;
        }
        .about-embedded .about-contact-grid {
          grid-template-columns: 1fr 1fr !important;
          gap: 12px !important;
        }
        .about-embedded .about-divider {
          margin: 8px 0 !important;
        }
      `}</style>
      <div className={embedded ? 'about-embedded' : 'about-page'} style={{
        ...(!embedded ? { minHeight: '100vh' } : {}),
        // On mobile the top collage is hidden, so the page gradient's dark top
        // stop (#0a1538) showed as a darker band that didn't match the lighter
        // mid-page. Start at the mid-tone there so the top blends with the rest.
        background: isMobileBundle
          ? 'linear-gradient(180deg, #102650 0%, #0e1d48 45%, #0c1740 80%, #0a1538 100%)'
          : 'linear-gradient(180deg, #0a1538 0%, #0c1740 10%, #0e1d48 25%, #102650 40%, #0e1d48 60%, #0c1740 80%, #0a1538 100%)',
        color: 'white',
        fontFamily: font,
        position: 'relative',
      }}>
        {/* Memories collage — pre-baked composite (scripts/build-about-collage.py)
            so the rotated photos read as one continuous image with no
            inter-tile seams. Anchored to the top of the page; mirrors
            the homepage hero pattern where the drinks photo sits behind
            the heading. Rendered in embedded mode too so the in-app
            About page (SettingsModal IonModal) doesn't show a hard band
            between the modal background and the wrapper's gradient.
            Parallax is skipped for embedded since the modal scrolls on
            IonContent rather than window. */}
        {!isMobileBundle ? (
          <div className="about-collage">
            <img ref={collageRef} src="/collage-composite.png?v=10" alt="" style={{ willChange: 'transform' }} />
          </div>
        ) : (
          /* Mobile prunes the 3.4MB collage image. A soft purple aurora fills
             the same top band so it reads as an intentional backdrop (not a
             flat dark gap) and dissolves smoothly into the page gradient. */
          <div className="about-collage-fallback" aria-hidden="true" />
        )}
        {!embedded && <PageHeader />}
        <div className="about-content-wrap">

          {/* Mission — fade in, side-by-side on desktop. Top margin
              positions "Who we are" over the faded portion of the
              collage backdrop, matching where the old hero h1 sat. */}
          <div ref={missionFade.ref} className="about-mission-section" style={missionFade.style}>
            <div className="about-mission-text">
              <h1 className="about-mission-heading" style={{
                lineHeight: '1.3',
                fontWeight: '600',
                letterSpacing: '-0.02em',
                margin: '0 0 8px',
                fontFamily: 'var(--font-bricolage), -apple-system, BlinkMacSystemFont, "SF Pro Display", "Helvetica Neue", sans-serif',
                background: 'linear-gradient(90deg, #E8DEFF 0%, #E8DEFF 40%, #FFFFFF 50%, #E8DEFF 60%, #E8DEFF 100%)',
                backgroundSize: '200% auto',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                color: 'transparent',
                  }}>
                Who we are
              </h1>
              <p className="about-mission-subheading">We believe the best part of life happens together.</p>
              <p className="about-mission-body" style={{
                lineHeight: '1.65',
                color: 'rgba(255, 255, 255, 0.85)',
                margin: 0,
              }}>
                The long dinner where nobody checks their phone. The friends from different corners of your life finally meeting and somehow hitting it off. The bachelorette trip with a million laughs (and a thousand receipts).
              </p>
              <p className="about-mission-body" style={{
                lineHeight: '1.65',
                fontWeight: 600,
                margin: '16px 0 0',
                background: 'linear-gradient(90deg, #E8DEFF 0%, #E8DEFF 40%, #FFFFFF 50%, #E8DEFF 60%, #E8DEFF 100%)',
                backgroundSize: '200% auto',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                color: 'transparent',
                animation: 'aboutSparkle 6s ease-in-out infinite',
              }}>
                These moments are what make us better — kinder, happier, and more ourselves.
              </p>
              <p className="about-mission-body" style={{
                lineHeight: '1.65',
                color: 'rgba(255, 255, 255, 0.85)',
                margin: '16px 0 0',
              }}>
                We&apos;re building tools that quietly remove the friction from gathering. Because your time is better spent making fun plans, forming new connections, and spending time with loved ones.
              </p>
            </div>
            <div className="about-founders-wrap" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: '24px', position: 'relative', zIndex: 0 }}>
              <canvas ref={foundersConfettiRef} className="founders-confetti-canvas" aria-hidden="true" />
              <div className="founders-video-wrap">
                {/* poster: without one there is no thumbnail for Google to
                    associate with the clip, and a video with no reachable
                    thumbnail cannot be indexed — these showed up in Search
                    Console's "No video indexed" bucket. It also gives the
                    element something to paint before the file arrives
                    instead of a black box. */}
                <video
                  className="founders-video"
                  src="/about-video.mp4"
                  poster="/about-video-poster.jpg"
                  autoPlay
                  loop
                  muted
                  playsInline
                  aria-label="Tabster co-founders intro video"
                />
              </div>
              <p className="founders-caption">McKinley, Founder &amp; CEO</p>
              <p className="founders-caption-sub">McKinley&apos;s Tabster feed · May 2026</p>
            </div>
          </div>

          {/* Phone mockup + tagline — fade in */}
          <div ref={phoneFade.ref} className="about-phone-section" style={{
            ...phoneFade.style,
            display: 'flex',
            alignItems: 'center',
            marginBottom: '8px',
          }}>
            <div className="about-phone-mockup" style={{
              overflow: 'hidden',
              borderRadius: '28px',
              animation: 'phonePulse 3s ease-in-out infinite',
            }}>
              {/* Same clip as the landing hero, so it reuses that poster
                  rather than minting a second copy of the same frame. */}
              <video
                src="/items-pay.mp4"
                poster="/items-pay-poster.jpg"
                autoPlay
                loop
                muted
                playsInline
                aria-label="Tabster app demo: tap items to claim"
                style={{
                  width: '100%',
                  height: 'auto',
                  display: 'block',
                }}
              />
            </div>
            <div className="about-phone-text" style={{ flex: 1 }}>
              <h2 className="about-phone-heading" style={{
                fontWeight: '600',
                lineHeight: '1.3',
                letterSpacing: '-0.02em',
                margin: '0 0 8px',
                fontFamily: 'var(--font-bricolage), -apple-system, BlinkMacSystemFont, "SF Pro Display", "Helvetica Neue", sans-serif',
                background: 'linear-gradient(90deg, #E8DEFF 0%, #E8DEFF 40%, #FFFFFF 50%, #E8DEFF 60%, #E8DEFF 100%)',
                backgroundSize: '200% auto',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                color: 'transparent',
                  }}>
                What we built
              </h2>
              <p className="about-phone-subheading">An AI-generated, interactive receipt for the group tab coverer.</p>
              <p className="about-phone-body" style={{
                lineHeight: '1.65',
                color: 'rgba(255, 255, 255, 0.85)',
                margin: 0,
              }}>
                The friend who put their card down just snaps a pic of the receipt and shares in seconds. So they can stay engaged in the great conversation at the table instead of doing math on the side.
              </p>
              <p className="about-phone-body" style={{
                lineHeight: '1.65',
                fontWeight: 600,
                margin: '16px 0 0',
                background: 'linear-gradient(90deg, #E8DEFF 0%, #E8DEFF 40%, #FFFFFF 50%, #E8DEFF 60%, #E8DEFF 100%)',
                backgroundSize: '200% auto',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                color: 'transparent',
                animation: 'aboutSparkle 6s ease-in-out infinite',
              }}>
                Showing up for the people you love is always worth it.
              </p>
              <p className="about-phone-body" style={{
                lineHeight: '1.65',
                color: 'rgba(255, 255, 255, 0.85)',
                margin: '16px 0 0',
              }}>
                And we applaud the group dinner organizers, trip planners, and tab coverers in our lives who facilitate these special moments.
              </p>
              <p className="about-phone-body" style={{
                lineHeight: '1.65',
                color: 'rgba(255, 255, 255, 0.85)',
                margin: '16px 0 0',
              }}>
                We know how generous you are, as the admin of getting people together can quietly eat the time spent being together. We want you to reap the benefits of your generosity without the hassle.
              </p>
            </div>
          </div>


          <GradientDivider />

          {/* Get in touch — fade in */}
          <div ref={contactFade.ref} className="about-contact-section" style={{ ...contactFade.style, marginBottom: '48px', paddingTop: '20px' }}>
            <h2 className="about-section-heading" style={{
              fontWeight: '600',
              marginBottom: '8px',
              textAlign: 'center',
              letterSpacing: '-0.02em',
              fontFamily: 'var(--font-bricolage), -apple-system, BlinkMacSystemFont, "SF Pro Display", "Helvetica Neue", sans-serif',
              background: 'linear-gradient(90deg, #E8DEFF 0%, #E8DEFF 40%, #FFFFFF 50%, #E8DEFF 60%, #E8DEFF 100%)',
              backgroundSize: '200% auto',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              color: 'transparent',
              }}>
              Get in touch
            </h2>
            <p className="about-mission-body" style={{
              lineHeight: '1.65',
              color: 'white',
              fontWeight: '600',
              margin: '0 0 20px',
              textAlign: 'center',
            }}>
              Questions, feedback, or just want to say hello?
            </p>

            <div className="about-contact-grid" style={{ display: 'flex', flexWrap: 'wrap', gap: '24px', justifyContent: 'center' }}>
              <a href="https://www.instagram.com/tabstertabs" target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: '8px', textDecoration: 'none', color: 'rgba(255,255,255,0.7)', fontSize: '14px', fontWeight: '500', transition: 'color 0.2s ease' }} onMouseEnter={e => e.currentTarget.style.color = 'white'} onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.7)'}>
                <InstagramIcon />
                <span>@tabstertabs</span>
              </a>
              <a href="https://www.tiktok.com/@tabstertabs" target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: '8px', textDecoration: 'none', color: 'rgba(255,255,255,0.7)', fontSize: '14px', fontWeight: '500', transition: 'color 0.2s ease' }} onMouseEnter={e => e.currentTarget.style.color = 'white'} onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.7)'}>
                <TikTokIcon />
                <span>@tabstertabs</span>
              </a>
              <a href="mailto:support@tabster.ai" style={{ display: 'flex', alignItems: 'center', gap: '8px', textDecoration: 'none', color: 'rgba(255,255,255,0.7)', fontSize: '14px', fontWeight: '500', transition: 'color 0.2s ease' }} onMouseEnter={e => e.currentTarget.style.color = 'white'} onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.7)'}>
                <MailIcon />
                <span>support@tabster.ai</span>
              </a>
              <a href="/tips" style={{ display: 'flex', alignItems: 'center', gap: '8px', textDecoration: 'none', color: 'rgba(255,255,255,0.7)', fontSize: '14px', fontWeight: '500', transition: 'color 0.2s ease' }} onMouseEnter={e => e.currentTarget.style.color = 'white'} onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.7)'}>
                <BlogIcon />
                <span>Tips by Tabster</span>
              </a>
            </div>
          </div>

        </div>

        {!embedded && <PageFooter />}

      </div>
    </>
  );
};

export default AboutContent;
